import type { Alignment, Driver, Side } from 'driver.js';
import type { PopoverDimensions } from './types';

export const getPopoverDimensions = (driver: Driver): PopoverDimensions | undefined => {
  const popover = document.querySelector('.driver-popover') as HTMLElement;
  if (!popover) {
    return;
  }

  const config = driver.getConfig();

  const boundingClientRect = popover.getBoundingClientRect();

  const stagePadding = config.stagePadding || 0;
  const popoverOffset = config.popoverOffset || 0;

  return {
    width: boundingClientRect.width + stagePadding + popoverOffset,
    height: boundingClientRect.height + stagePadding + popoverOffset,

    realWidth: boundingClientRect.width,
    realHeight: boundingClientRect.height,
  };
};

export const renderPopoverArrow = (
  driver: Driver,
  element: HTMLElement,
  popover: HTMLElement,
  alignment: Alignment,
  side: Side,
) => {
  const config = driver.getConfig();
  const elementDimensions = element.getBoundingClientRect();

  const popoverDimensions = getPopoverDimensions(driver);
  if (!popoverDimensions) {
    return;
  }

  const popoverArrow = document.querySelector('.driver-popover-arrow');
  if (!popoverArrow) {
    return;
  }

  const popoverWidth = popoverDimensions.width;
  const windowWidth = window.innerWidth;
  const elementWidth = elementDimensions.width;
  const elementLeft = elementDimensions.left;

  const popoverHeight = popoverDimensions.height;
  const windowHeight = window.innerHeight;
  const elementTop = elementDimensions.top;
  const elementHeight = elementDimensions.height;

  // Remove all arrow classes
  popoverArrow.className = 'driver-popover-arrow';

  let arrowSide = side;
  let arrowAlignment = alignment;

  if (side === 'top') {
    if (elementLeft + elementWidth <= 0) {
      arrowSide = 'right';
      arrowAlignment = 'end';
    } else if (elementLeft + elementWidth - popoverWidth <= 0) {
      arrowSide = 'top';
      arrowAlignment = 'start';
    }
    if (elementLeft >= windowWidth) {
      arrowSide = 'left';
      arrowAlignment = 'end';
    } else if (elementLeft + popoverWidth >= windowWidth) {
      arrowSide = 'top';
      arrowAlignment = 'end';
    }
  } else if (side === 'bottom') {
    if (elementLeft + elementWidth <= 0) {
      arrowSide = 'right';
      arrowAlignment = 'start';
    } else if (elementLeft + elementWidth - popoverWidth <= 0) {
      arrowSide = 'bottom';
      arrowAlignment = 'start';
    }
    if (elementLeft >= windowWidth) {
      arrowSide = 'left';
      arrowAlignment = 'start';
    } else if (elementLeft + popoverWidth >= windowWidth) {
      arrowSide = 'bottom';
      arrowAlignment = 'end';
    }
  } else if (side === 'left') {
    if (elementTop + elementHeight <= 0) {
      arrowSide = 'bottom';
      arrowAlignment = 'end';
    } else if (elementTop + elementHeight - popoverHeight <= 0) {
      arrowSide = 'left';
      arrowAlignment = 'start';
    }

    if (elementTop >= windowHeight) {
      arrowSide = 'top';
      arrowAlignment = 'end';
    } else if (elementTop + popoverHeight >= windowHeight) {
      arrowSide = 'left';
      arrowAlignment = 'end';
    }
  } else if (side === 'right') {
    if (elementTop + elementHeight <= 0) {
      arrowSide = 'bottom';
      arrowAlignment = 'start';
    } else if (elementTop + elementHeight - popoverHeight <= 0) {
      arrowSide = 'right';
      arrowAlignment = 'start';
    }

    if (elementTop >= windowHeight) {
      arrowSide = 'top';
      arrowAlignment = 'start';
    } else if (elementTop + popoverHeight >= windowHeight) {
      arrowSide = 'right';
      arrowAlignment = 'end';
    }
  }

  if (!arrowSide) {
    popoverArrow.classList.add('driver-popover-arrow-none');
  } else {
    popoverArrow.classList.add(`driver-popover-arrow-side-${arrowSide}`);
    popoverArrow.classList.add(`driver-popover-arrow-align-${arrowAlignment}`);

    const elementRect = element.getBoundingClientRect();
    const arrowRect = popoverArrow.getBoundingClientRect();
    const stagePadding = config.stagePadding || 0;

    const isElementPartiallyInViewPort =
      elementRect.left - stagePadding < window.innerWidth &&
      elementRect.right + stagePadding > 0 &&
      elementRect.top - stagePadding < window.innerHeight &&
      elementRect.bottom + stagePadding > 0;

    if (side === 'bottom' && isElementPartiallyInViewPort) {
      const isArrowWithinElementBounds =
        arrowRect.x > elementRect.x && arrowRect.x + arrowRect.width < elementRect.x + elementRect.width;

      if (!isArrowWithinElementBounds) {
        popoverArrow.classList.remove(`driver-popover-arrow-align-${arrowAlignment}`);
        popoverArrow.classList.add(`driver-popover-arrow-none`);
        popover.style.transform = `translateY(-${stagePadding / 2}px)`;
      } else {
        popover.style.transform = `translateY(0)`;
      }
    }
  }
};

export const calculateTopForLeftRight = (
  alignment: Alignment,
  config: {
    elementDimensions: DOMRect;
    popoverDimensions: PopoverDimensions;
    popoverPadding: number;
    popoverArrowDimensions: { width: number; height: number };
  },
): number => {
  const { elementDimensions, popoverDimensions, popoverPadding, popoverArrowDimensions } = config;

  if (alignment === 'start') {
    return Math.max(
      Math.min(
        elementDimensions.top - popoverPadding,
        window.innerHeight - popoverDimensions.realHeight - popoverArrowDimensions.width,
      ),
      popoverArrowDimensions.width,
    );
  }

  if (alignment === 'end') {
    return Math.max(
      Math.min(
        elementDimensions.top - popoverDimensions?.realHeight + elementDimensions.height + popoverPadding,
        window.innerHeight - popoverDimensions?.realHeight - popoverArrowDimensions.width,
      ),
      popoverArrowDimensions.width,
    );
  }

  if (alignment === 'center') {
    return Math.max(
      Math.min(
        elementDimensions.top + elementDimensions.height / 2 - popoverDimensions?.realHeight / 2,
        window.innerHeight - popoverDimensions?.realHeight - popoverArrowDimensions.width,
      ),
      popoverArrowDimensions.width,
    );
  }

  return 0;
};

export const calculateLeftForTopBottom = (
  alignment: Alignment,
  config: {
    elementDimensions: DOMRect;
    popoverDimensions: PopoverDimensions;
    popoverPadding: number;
    popoverArrowDimensions: { width: number; height: number };
  },
): number => {
  const { elementDimensions, popoverDimensions, popoverPadding, popoverArrowDimensions } = config;

  if (alignment === 'start') {
    return Math.max(
      Math.min(
        elementDimensions.left - popoverPadding,
        window.innerWidth - popoverDimensions.realWidth - popoverArrowDimensions.width,
      ),
      popoverArrowDimensions.width,
    );
  }

  if (alignment === 'end') {
    return Math.max(
      Math.min(
        elementDimensions.left - popoverDimensions?.realWidth + elementDimensions.width + popoverPadding,
        window.innerWidth - popoverDimensions?.realWidth - popoverArrowDimensions.width,
      ),
      popoverArrowDimensions.width,
    );
  }

  if (alignment === 'center') {
    return Math.max(
      Math.min(
        elementDimensions.left + elementDimensions.width / 2 - popoverDimensions?.realWidth / 2,
        window.innerWidth - popoverDimensions?.realWidth - popoverArrowDimensions.width,
      ),
      popoverArrowDimensions.width,
    );
  }

  return 0;
};
